<script lang="ts">
export default {
  name: 'TableauModal',
  components: {
    AppSelect,
  },
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { tableauService, accountService } from '@/services/api';
import { reportingService } from '@/services/ReportingService';
import { TableauDashboard, TableauType } from '@/types/TableauDashboard';
import { SelectOption } from '@/types/components/Select';
import Combobox from '@/components/v2/Combobox.vue';
import { z } from 'zod';
import AppSelect from '@/components/v2/AppSelect.vue';
import { AlertCircle } from '@lucide/vue';

// ------
// props
// ------
const props = defineProps<{
  show: boolean;
  mode: 'upload' | 'edit';
  dashboard?: TableauDashboard;
  tableauDomain: string;
  enableAccountSelect?: boolean;
  tableauTypes?: TableauType[];
  accountId?: number;
  useReportingApi?: boolean;
}>();

// ------
// emits
// ------
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
}>();

// ---------
// computed
// ---------
const tableauDomain = computed(() => props.tableauDomain); // use computed prop here, or this can't be access in dashboardSchema

const selectableTableauTypes = computed(() => {
  return props.tableauTypes?.map((type) => {
    return {
      label: type.name,
      value: type.id,
    };
  });
});

// -----
// refs
// -----
const showModal = ref<boolean>(false);
const dashboardName = ref<string>('');
const dashboardUrl = ref<string>('');
const dashboardTypeId = ref<number>();
const nameError = ref<string>('');
const urlError = ref<string>('');
const typeError = ref<string>('');
const submitError = ref<string>('');
const accounts = ref<SelectOption[]>([]);
const selectedAccount = ref('');

const dashboardSchema = z.object({
  name: z.string().nonempty('A name is required').max(150, 'Name cannot exceed 150 characters'),
  url: z
    .string()
    .nonempty('A URL is required')
    .url('Invalid URL format')
    .refine((data) => data.startsWith('https://'), {
      message: "URL must start with 'https://'",
    })
    .refine(
      (data) => {
        try {
          const urlObj = new URL(data);
          return urlObj.hostname.includes(tableauDomain.value);
        } catch (e) {
          return false;
        }
      },
      {
        message: `URL's domain must be ${tableauDomain.value}`,
      },
    ),
});

// ---------
// watchers
// ---------
watch(
  () => props.show,
  (newValue) => {
    showModal.value = newValue;
    submitError.value = '';
    if (props.mode === 'edit' && props.dashboard) {
      dashboardName.value = props.dashboard.name;
      dashboardUrl.value = props.dashboard.dashboardUrl;
      dashboardTypeId.value = props.dashboard.tableauTypeId;
      selectedAccount.value = props.dashboard?.accountId?.toString() || '';
    } else {
      dashboardName.value = '';
      dashboardUrl.value = '';
    }
  },
);

// --------
// methods
// --------
async function handleSave() {
  validateName();
  validateUrl();
  validateType();

  if (nameError.value || urlError.value || !dashboardTypeId.value) {
    return;
  }

  submitError.value = '';

  try {
    let accountId: number | null = null;
    if (props.enableAccountSelect) {
      const parsed = parseInt(selectedAccount.value);
      accountId = Number.isNaN(parsed) ? null : parsed;
    } else if (props.accountId) {
      accountId = props.accountId;
    }

    if (props.mode === 'edit' && props.dashboard) {
      if (props.useReportingApi) {
        await reportingService.updateReportingTableauDashboard(props.dashboard.id, {
          name: dashboardName.value,
          dashboardUrl: dashboardUrl.value,
          tableauTypeId: dashboardTypeId.value,
        });
      } else {
        await tableauService.updateDashboard(props.dashboard.id, dashboardName.value, dashboardUrl.value, dashboardTypeId.value, accountId);
      }
    } else {
      await tableauService.createDashboard(dashboardName.value, dashboardUrl.value, dashboardTypeId.value, accountId);
    }
    clearDataFields();
    showModal.value = false;
    emit('save');
    emit('close');
  } catch (error) {
    console.error(error);
    submitError.value = error instanceof Error ? error.message : 'Failed to save dashboard.';
  }
}

function handleClose() {
  clearDataFields();
  showModal.value = false;
  emit('close');
}

function clearDataFields() {
  dashboardName.value = '';
  dashboardUrl.value = '';
  dashboardTypeId.value = 0;
  nameError.value = '';
  urlError.value = '';
  submitError.value = '';
  selectedAccount.value = '';
}

function validateName() {
  const result = dashboardSchema.safeParse({
    name: dashboardName.value,
  });

  if (result.success) {
    nameError.value = '';
  } else {
    nameError.value = result.error?.errors.find((e) => e.path[0] === 'name')?.message || '';
  }
}

function validateUrl() {
  const result = dashboardSchema.safeParse({
    url: dashboardUrl.value,
  });

  if (result.success) {
    urlError.value = '';
  } else {
    urlError.value = result.error?.errors.find((e) => e.path[0] === 'url')?.message || '';
  }
}

function validateType() {
  if (!dashboardTypeId.value) {
    typeError.value = 'A type is required';
  } else {
    typeError.value = '';
  }
}

// -----------
// lifecycles
// -----------
onMounted(async () => {
  showModal.value = props.show;

  if (props.enableAccountSelect) {
    const accountList = await accountService.list();
    accounts.value = accountList.map((account) => {
      return {
        label: account.accountName,
        value: account.accountID.toString(),
      };
    });
  }
});
</script>

<template>
  <dialog
    class="modal"
    :class="showModal && 'modal-open'">
    <div class="form-control modal-box flex flex-col gap-4 overflow-visible">
      <h2 class="pb-3">{{ props.mode === 'edit' ? 'Edit' : 'Upload' }} Dashboard</h2>
      <div class="flex flex-col gap-6">
        <div>
          <label class="label">
            <span class="label-text pb-1 text-xs text-gray-500">Name</span>
          </label>
          <input
            type="text"
            placeholder=""
            class="input input-md w-full"
            v-model="dashboardName"
            @blur="validateName" />
          <span class="text-red-500">{{ nameError }}</span>
        </div>
        <div>
          <label class="label">
            <span class="label-text pb-1 text-xs text-gray-500">URL</span>
          </label>
          <input
            type="text"
            placeholder=""
            class="input input-md w-full"
            v-model="dashboardUrl"
            @blur="validateUrl" />
          <span class="text-red-500">{{ urlError }}</span>
        </div>
        <div>
          <label class="label">
            <span class="label-text pb-1 text-xs text-gray-500">Type</span>
          </label>
          <AppSelect
            v-model="dashboardTypeId"
            :options="selectableTableauTypes"
            class="h-9 w-full"
            placeholder="Select type"
            @update:modelValue="validateType" />
          <span class="text-red-500">{{ typeError }}</span>
        </div>
      </div>

      <!-- ACCOUNT-->
      <div
        v-if="props.enableAccountSelect"
        class="w-full">
        <div class="flex flex-col">
          <label class="label">
            <span class="label-text pb-1 text-xs text-gray-500">Account</span>
          </label>
          <Combobox
            v-model="selectedAccount"
            :options="accounts"
            :placeholder="'Select Account'" />
        </div>
      </div>

      <div class="flex gap-4"></div>
      <div
        v-if="submitError"
        role="alert"
        class="alert alert-error">
        <AlertCircle class="h-5 w-5 shrink-0" />
        <span>{{ submitError }}</span>
      </div>
      <div class="modal-action">
        <button
          class="btn btn-sm"
          @click="handleClose">
          Cancel
        </button>
        <button
          class="btn btn-sm btn-primary"
          @click="handleSave">
          Save
        </button>
      </div>
    </div>
  </dialog>
</template>
